import { IssueSourceType } from '@gooduncles/gu-app-schema';
import { keyBy, mapValues } from 'lodash-es';
import { FC, useCallback, useMemo } from 'react';

import useIssueCaseById from 'src/hooks/subscription/useIssueCaseById';
import useIssueCasesForCase from 'src/hooks/subscription/useIssueCasesForCase';
import useIssueCasesForOrder from 'src/hooks/subscription/useIssueCasesForOrder';
import useOrderById from 'src/hooks/swr/useOrderById';
import useStoreById from 'src/hooks/swr/useStoreById';
import { useScrollToElement } from 'src/hooks/useScrollToElement';

import columnStyles from 'src/atomic-components/atoms/Column/Column.module.scss';
import AddressPopover from 'src/atomic-components/molecules/AddressPopover/AddressPopover';

import styles from './IssuePanel.module.scss';

import ErrorMessage from './ErrorMessage';
import IssueColumnHeader from './IssueColumnHeader';
import IssueEditSection from './IssueEditSection';
import IssueTargetSection from './IssueTargetSection';
import LoadingSpinner from './LoadingSpinner';

/**
 * - 주문을 눌러서 이슈를 추가하는 경우 (FromOrder)
 * - 이슈를 눌러서 이슈를 추가하는 경우 (FromIssue - sourceType/sourceId 있음)
 * - 이슈를 새로 생성하는 경우 (NewIssue)
 * - 연결된 주문/이슈 없이 직접 등록된 이슈를 조회하는 경우 (StandaloneIssue)
 */
type IssuePanelProps =
  | { orderId: string; issueId?: never; storeId?: never; standaloneIssueId?: never } // FromOrder
  | { orderId?: never; issueId: string; storeId?: never; standaloneIssueId?: never } // FromIssue
  | { orderId?: never; issueId?: never; storeId: string; standaloneIssueId?: never } // NewIssue
  | { orderId?: never; issueId?: never; storeId?: never; standaloneIssueId: string }; // StandaloneIssue

const IssuePanel: FC<IssuePanelProps> = ({ orderId, issueId, storeId, standaloneIssueId }) => {
  const { order, orderError } = useOrderById(orderId);
  const { issue, isLoading: issueLoading } = useIssueCaseById(issueId);
  const { issue: standaloneIssue, isLoading: standaloneIssueLoading } = useIssueCaseById(standaloneIssueId);
  const { store, storeError } = useStoreById(order?.storeId || issue?.storeId || standaloneIssue?.storeId || storeId);

  // 시나리오별 이슈 케이스 데이터 fetching
  const orderIssueCases = useIssueCasesForOrder(orderId);
  const issueRelatedCases = useIssueCasesForCase(issueId);

  // 조건부 데이터 통합 및 registeredAt 기준 오름차순 정렬
  const issueCases = useMemo(() => {
    // standaloneIssue인 경우 해당 이슈만 표시
    if (standaloneIssueId && standaloneIssue) {
      return [standaloneIssue];
    }
    const cases = orderId ? orderIssueCases : issueId ? issueRelatedCases : [];
    return [...cases].sort((a, b) => a.registeredAt.localeCompare(b.registeredAt));
  }, [orderId, orderIssueCases, issueId, issueRelatedCases, standaloneIssueId, standaloneIssue]);

  const parentProducts = orderId
    ? mapValues(keyBy(order?.products, 'productId'), 'volume')
    : issueId
      ? mapValues(keyBy(issue?.products, 'productId'), 'quantity')
      : undefined;

  const { containerRef: caseColumnRef, scrollTo: scrollToIssueCase } = useScrollToElement();

  const scrollToCaseCard = useCallback(
    (caseId: string) => {
      scrollToIssueCase(`case-card-${caseId}`);
    },
    [scrollToIssueCase],
  );

  // Early return으로 에러 및 로딩 상태 처리
  if (orderError) return <ErrorMessage error={orderError} />;
  if (storeError) return <ErrorMessage error={storeError} />;

  if (orderId && !store) {
    return <LoadingSpinner message='주문 정보를 불러오는 중...' />;
  }

  if (issueId && issueLoading) {
    return <LoadingSpinner message='이슈 정보를 불러오는 중...' />;
  }

  if (standaloneIssueId && standaloneIssueLoading) {
    return <LoadingSpinner message='이슈 정보를 불러오는 중...' />;
  }

  return (
    <div className={styles.issuePanel}>
      <div className={columnStyles.column}>
        {order && (
          <IssueColumnHeader>
            파트너: {order.deliveryPartnerName}
            {order.deliverySpotId && <AddressPopover deliverySpotId={order.deliverySpotId} storeId={order.storeId} />}
          </IssueColumnHeader>
        )}
        {issue && (
          <IssueColumnHeader tooltip={issue.sourceId ?? undefined}>
            이슈 대상: {issue.sourceReferenceDate} {issue.sourceType === IssueSourceType.ORDER ? '주문' : '이슈'}
          </IssueColumnHeader>
        )}
        {standaloneIssue && (
          <IssueColumnHeader tooltip={standaloneIssue.storeId ?? undefined}>
            단독 이슈건 ({standaloneIssue.storeName})
          </IssueColumnHeader>
        )}
        <IssueTargetSection
          order={order}
          issue={issue}
          standaloneIssue={standaloneIssue}
          store={store}
          issueCases={issueCases}
          onIssueCaseClick={scrollToCaseCard}
        />
      </div>

      <IssueEditSection issueCases={issueCases} parentProducts={parentProducts} caseColumnRef={caseColumnRef} />
    </div>
  );
};

export default IssuePanel;
export type { IssuePanelProps };
